import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2 } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { AButton, AField, aInput, StatusPill } from "./ui";

const emptyFromFields = (fields) =>
  Object.fromEntries(fields.map((f) => [f.name, f.type === "list" ? [] : f.type === "checkbox" ? true : f.type === "number" ? 0 : f.default ?? ""]));

const FieldInput = ({ field, value, onChange }) => {
  if (field.type === "textarea")
    return <textarea rows={field.rows || 3} className={aInput} value={value ?? ""} onChange={(e) => onChange(e.target.value)} data-testid={`field-${field.name}`} />;
  if (field.type === "list")
    return <textarea rows={field.rows || 4} className={`${aInput} font-mono text-xs`} value={(value || []).join("\n")}
                     onChange={(e) => onChange(e.target.value.split("\n").map((s) => s.trim()).filter(Boolean))}
                     placeholder="One per line" data-testid={`field-${field.name}`} />;
  if (field.type === "select")
    return (
      <select className={aInput} value={value ?? ""} onChange={(e) => onChange(e.target.value)} data-testid={`field-${field.name}`}>
        <option value="">Select…</option>
        {(field.options || []).map((o) => <option key={o} value={o}>{o}</option>)}
      </select>
    );
  if (field.type === "checkbox")
    return (
      <label className="flex items-center gap-2 text-sm text-tark-mut">
        <input type="checkbox" checked={!!value} onChange={(e) => onChange(e.target.checked)} data-testid={`field-${field.name}`} className="h-4 w-4 accent-tark-blue" />
        Published
      </label>
    );
  return <input type={field.type === "number" ? "number" : "text"} className={aInput} value={value ?? ""}
                onChange={(e) => onChange(field.type === "number" ? Number(e.target.value) : e.target.value)} data-testid={`field-${field.name}`} />;
};

const CrudTab = ({ endpoint, title, fields, columns, testId }) => {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyFromFields(fields));
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => {
    api.get(`/admin/${endpoint}`).then((r) => setItems(r.data)).catch((e) => toast.error(formatApiError(e)));
  }, [endpoint]);

  useEffect(() => { load(); }, [load]);

  const startNew = () => { setEditing("new"); setForm(emptyFromFields(fields)); };
  const startEdit = (item) => { setEditing(item.id); setForm({ ...emptyFromFields(fields), ...item }); };

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (editing === "new") await api.post(`/admin/${endpoint}`, form);
      else await api.put(`/admin/${endpoint}/${editing}`, form);
      toast.success("Saved.");
      setEditing(null);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this item?")) return;
    try {
      await api.delete(`/admin/${endpoint}/${id}`);
      toast.success("Deleted.");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  return (
    <div data-testid={`admin-tab-${testId}`}>
      <div className="flex items-center justify-between">
        <h2 className="font-display text-xl font-bold tracking-tight">{title}</h2>
        <AButton onClick={startNew} testId={`${testId}-add-btn`}><span className="inline-flex items-center gap-1.5"><Plus className="h-3.5 w-3.5" /> Add</span></AButton>
      </div>

      {editing && (
        <form onSubmit={save} className="mt-6 rounded-2xl border border-tark-blue/40 bg-tark-card p-6" data-testid={`${testId}-editor`}>
          <div className="grid gap-4 sm:grid-cols-2">
            {fields.map((f) => (
              <div key={f.name} className={f.type === "textarea" || f.type === "list" ? "sm:col-span-2" : ""}>
                <AField label={f.label}>
                  <FieldInput field={f} value={form[f.name]} onChange={(v) => setForm({ ...form, [f.name]: v })} />
                </AField>
              </div>
            ))}
          </div>
          <div className="mt-6 flex gap-3">
            <AButton type="submit" disabled={saving} testId={`${testId}-save-btn`}>{saving ? "Saving…" : "Save"}</AButton>
            <AButton variant="ghost" onClick={() => setEditing(null)} testId={`${testId}-cancel-btn`}>Cancel</AButton>
          </div>
        </form>
      )}

      <div className="mt-6 overflow-x-auto rounded-2xl border border-tark-line">
        <table className="w-full min-w-[560px] text-left text-sm">
          <thead>
            <tr className="border-b border-tark-line bg-tark-card font-mono text-[10px] uppercase tracking-[0.2em] text-tark-dim">
              {columns.map((c) => <th key={c.key} className="px-5 py-3.5 font-medium">{c.label}</th>)}
              <th className="px-5 py-3.5" />
            </tr>
          </thead>
          <tbody>
            {items.map((item) => (
              <tr key={item.id} className="border-b border-tark-line/50 last:border-0 hover:bg-tark-card/60" data-testid={`${testId}-row-${item.id}`}>
                {columns.map((c) => (
                  <td key={c.key} className="px-5 py-3.5 text-tark-mut">
                    {c.key === "status" ? <StatusPill status={item.status} /> : String(item[c.key] ?? "")}
                  </td>
                ))}
                <td className="px-5 py-3.5 text-right">
                  <button onClick={() => startEdit(item)} className="mr-3 font-mono text-xs text-tark-cyan hover:underline" data-testid={`${testId}-edit-${item.id}`}>Edit</button>
                  <button onClick={() => remove(item.id)} className="text-red-400/70 hover:text-red-400" data-testid={`${testId}-delete-${item.id}`}><Trash2 className="h-3.5 w-3.5" /></button>
                </td>
              </tr>
            ))}
            {items.length === 0 && (
              <tr><td colSpan={columns.length + 1} className="px-5 py-10 text-center font-mono text-xs text-tark-dim">No entries yet.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default CrudTab;
